"use client"

import { memo, useEffect, useState } from "react"
import axios from "axios"
import { useScreenSize } from "@/hooks/useScreenSize"

function scheduleWhenIdle(callback, timeout = 400) {
  if (typeof window === "undefined") return () => {}

  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(() => callback(), { timeout })
    return () => window.cancelIdleCallback(id)
  }

  const id = window.setTimeout(callback, 250)
  return () => window.clearTimeout(id)
}

function Advertisement({
  page,
  position,
  className = "",
  style,
  wrapperClassName = "",
  fallback = null,
}) {
  const { screenWidth } = useScreenSize()
  const [shouldLoad, setShouldLoad] = useState(false)
  const [ad, setAd] = useState(null)

  // Idle-deferred fetch (no IntersectionObserver — empty slots never intersect)
  useEffect(() => {
    if (!page) return

    let cancelled = false
    const cancelIdle = scheduleWhenIdle(() => {
      if (!cancelled) setShouldLoad(true)
    })

    return () => {
      cancelled = true
      cancelIdle()
    }
  }, [page, position])

  useEffect(() => {
    if (!shouldLoad || !page) return

    let cancelled = false

    const fetchAd = async () => {
      try {
        const params = { page }
        if (position) params.position = position

        const response = await axios.get("/api/advertisements", { params })

        if (!cancelled && response.data.success) {
          setAd(response.data.data?.[0] || null)
        }
      } catch {
        if (!cancelled) setAd(null)
      }
    }

    fetchAd()

    return () => {
      cancelled = true
    }
  }, [shouldLoad, page, position])

  const isMobile = screenWidth <= 768
  const imageUrl = ad
    ? isMobile && ad.mobileImage
      ? ad.mobileImage
      : ad.image
    : null

  if (!(ad && imageUrl)) {
    return fallback || null
  }

  return (
    <div className={wrapperClassName || undefined}>
      {ad.clickUrl ? (
        <a
          href={ad.clickUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="block"
          aria-label={ad.title || "Advertisement"}
        >
          <img
            src={imageUrl}
            alt={ad.title || "Advertisement"}
            className={`block w-full h-auto object-cover ${className}`}
            style={style}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
          />
        </a>
      ) : (
        <img
          src={imageUrl}
          alt={ad.title || "Advertisement"}
          className={`block w-full h-auto object-cover ${className}`}
          style={style}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
        />
      )}
    </div>
  )
}

export default memo(Advertisement)
